import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminVendorActivity } from "../lib/api";

const usd = (n, ccy) =>
  n == null ? "—" : `$${Number(n).toLocaleString("en-US", { maximumFractionDigits: 2 })}${ccy && ccy !== "usd" ? ` ${ccy.toUpperCase()}` : ""}`;

const when = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  return isNaN(d) ? String(s).slice(0, 16) : d.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
};

function Stat({ label, value, testid }) {
  return (
    <div className="border border-[#000] px-3 py-2" data-testid={testid}>
      <div className="text-[22px] font-bold leading-none">{value}</div>
      <div className="text-[11px] text-[#666] mt-1">{label}</div>
    </div>
  );
}

function Section({ title, count, children, testid }) {
  return (
    <div className="mt-8" data-testid={testid}>
      <div className="flex items-baseline gap-2 border-b-2 border-[#000] pb-1 mb-2">
        <h2 className="text-[13px] font-bold uppercase tracking-wide">{title}</h2>
        <span className="text-[11px] text-[#888]">({count})</span>
      </div>
      {count === 0 ? (
        <div className="text-[12px] text-[#888] py-3" data-testid={`${testid}-empty`}>nothing yet.</div>
      ) : (
        <div className="overflow-x-auto">{children}</div>
      )}
    </div>
  );
}

const th = "text-left py-1 pr-4 text-[11px] uppercase text-[#666] font-bold whitespace-nowrap";
const td = "py-1 pr-4 align-top whitespace-nowrap";

export default function AdminActivity() {
  const [d, setD] = useState(null);
  const load = () =>
    adminVendorActivity()
      .then(setD)
      .catch(() => toast.error("could not load owner activity"));
  useEffect(() => {
    load();
  }, []);

  if (!d) return <div className="text-[13px] text-[#888]" data-testid="admin-activity-loading">loading…</div>;

  const { upgrades = [], sponsors = [], clicks = [], visits = [], totals = {} } = d;

  return (
    <div className="text-[13px]" data-testid="admin-activity">
      <div className="flex items-center justify-between mb-4">
        <p className="text-[12px] text-[#666] max-w-xl">
          Who upgraded, who bought sponsored slots, who clicked an email link to their listing, and who signed into
          the vendor dashboard. Newest first.
        </p>
        <button
          onClick={load}
          className="tv-cat-pill whitespace-nowrap"
          data-testid="admin-activity-refresh"
        >
          refresh
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat label="starter upgrades" value={totals.upgrades ?? 0} testid="admin-activity-stat-upgrades" />
        <Stat label="active sponsors" value={totals.sponsors_active ?? 0} testid="admin-activity-stat-sponsors" />
        <Stat label="email link clicks" value={totals.clicks ?? 0} testid="admin-activity-stat-clicks" />
        <Stat label="dashboard sessions" value={totals.dashboard_sessions ?? 0} testid="admin-activity-stat-sessions" />
      </div>

      <Section title="Starter upgrades" count={upgrades.length} testid="admin-activity-upgrades">
        <table className="w-full"><thead><tr>
          <th className={th}>when</th><th className={th}>owner</th><th className={th}>tool</th>
          <th className={th}>plan</th><th className={th}>amount</th>
        </tr></thead><tbody>
          {upgrades.map((u, i) => (
            <tr key={i} className="border-b border-[#eee]" data-testid={`admin-activity-upgrade-${i}`}>
              <td className={td}>{when(u.at)}</td>
              <td className={td}>{u.email || "—"}</td>
              <td className={td}>{u.slug || "—"}</td>
              <td className={td}>starter / {u.interval}</td>
              <td className={td}>{usd(u.amount, u.currency)}</td>
            </tr>
          ))}
        </tbody></table>
      </Section>

      <Section title="Sponsored slot purchases" count={sponsors.length} testid="admin-activity-sponsors">
        <table className="w-full"><thead><tr>
          <th className={th}>when</th><th className={th}>tool</th><th className={th}>placement</th>
          <th className={th}>term</th><th className={th}>price</th><th className={th}>status</th><th className={th}>ends</th>
        </tr></thead><tbody>
          {sponsors.map((s, i) => (
            <tr key={i} className="border-b border-[#eee]" data-testid={`admin-activity-sponsor-${i}`}>
              <td className={td}>{when(s.created_at)}</td>
              <td className={td}>{s.title || s.slug || "—"}</td>
              <td className={td}>{s.placement_label || "—"}</td>
              <td className={td}>{s.months}mo</td>
              <td className={td}>{usd(s.price_usd)}</td>
              <td className={td}>
                <span className={s.status === "active" ? "font-bold" : "text-[#b91c1c]"}>{s.status}</span>
                {s.source ? <span className="text-[#888]"> · {s.source}</span> : null}
              </td>
              <td className={td}>{String(s.ends_at || "").slice(0, 10) || "—"}</td>
            </tr>
          ))}
        </tbody></table>
      </Section>

      <Section title="Email link clicks" count={clicks.length} testid="admin-activity-clicks">
        <table className="w-full"><thead><tr>
          <th className={th}>when</th><th className={th}>recipient</th><th className={th}>campaign</th>
          <th className={th}>tool</th><th className={th}>went to</th>
        </tr></thead><tbody>
          {clicks.map((c, i) => (
            <tr key={i} className="border-b border-[#eee]" data-testid={`admin-activity-click-${i}`}>
              <td className={td}>{when(c.ts)}</td>
              <td className={td}>{c.email || "—"}</td>
              <td className={td}>{c.campaign || "—"}</td>
              <td className={td}>{c.slug || "—"}</td>
              <td className={td}>{c.path || "—"}</td>
            </tr>
          ))}
        </tbody></table>
      </Section>

      <Section title="Dashboard logins" count={visits.length} testid="admin-activity-visits">
        <table className="w-full"><thead><tr>
          <th className={th}>owner</th><th className={th}>day</th><th className={th}>first</th>
          <th className={th}>last</th><th className={th}>sessions</th>
        </tr></thead><tbody>
          {visits.map((v, i) => (
            <tr key={i} className="border-b border-[#eee]" data-testid={`admin-activity-visit-${i}`}>
              <td className={td}>{v.email || "—"}</td>
              <td className={td}>{v.day || "—"}</td>
              <td className={td}>{when(v.first_at)}</td>
              <td className={td}>{when(v.last_at)}</td>
              <td className={td}>{v.n ?? 0}</td>
            </tr>
          ))}
        </tbody></table>
      </Section>
    </div>
  );
}
